/**
 * ALTIORA · Growth & Media Engine — Console d'administration
 *
 * Centre de pilotage du moteur média : génération, bibliothèque, calendrier,
 * approbation, publication, A/B testing, recycling, avatar vidéo,
 * dashboard, journal, contrôles d'automatisation.
 */
import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import {
  Sparkles, LibraryBig, Calendar, Gauge, ScrollText, Video, Wand2,
  RefreshCw, Search, TrendingUp, CheckCircle2, Send, Beaker, Filter,
  BarChart3, Trophy, Clock,
} from "lucide-react";
import {
  PageShell, PageHeader, Card, PrimaryButton, GhostButton, FormField,
  Input, Alert, Loader, NAVY, CHAMPAGNE, SLATE, NAVY_DARK as INK,
} from "@/components/altiora/PremiumUI";
import { TopBar } from "@/components/layout/TopBar";
import { api } from "@/lib/api";

// ── Tabs ──────────────────────────────────────────────────────────────

const TABS = [
  { key: "generator",  label: "Générateur",  icon: Sparkles },
  { key: "library",    label: "Bibliothèque", icon: LibraryBig },
  { key: "calendar",   label: "Calendrier",   icon: Calendar },
  { key: "avatar",     label: "Avatar",       icon: Video },
  { key: "top",        label: "Top perfs",    icon: Trophy },
  { key: "dashboard",  label: "Tableau de bord", icon: BarChart3 },
  { key: "events",     label: "Journal",      icon: ScrollText },
  { key: "controls",   label: "Automatisation", icon: Gauge },
];

const STATUS_COLORS = {
  draft: "#6b7c93", approved: "#0a7f42", scheduled: "#c9a34e",
  published: "#0a2540", failed: "#c95050", rejected: "#8B4E00",
};

const CHANNEL_LABELS = {
  linkedin: "LinkedIn", facebook: "Facebook", instagram: "Instagram",
  x: "X (Twitter)", youtube: "YouTube", tiktok: "TikTok",
  email: "Email", internal: "Article interne",
};

// ── Root ─────────────────────────────────────────────────────────────

export default function AdminGrowthMedia() {
  const [tab, setTab] = useState("generator");

  return (
    <PageShell>
      <TopBar />
      <div className="max-w-[1400px] mx-auto px-6 py-8" data-testid="admin-growth-root">
        <PageHeader
          eyebrow="ALTIORA · Croissance & Média"
          title="Growth & Media Engine"
          subtitle="Du Knowledge Engine au contenu monétisable — génération, adaptation par canal, scoring, publication, apprentissage."
          testid="admin-growth-header"
        />

        <div className="flex flex-wrap gap-2 mt-6 mb-6" data-testid="admin-growth-tabs">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`growth-tab-${key}`}
              className="px-4 py-2 rounded-full text-sm font-medium transition flex items-center gap-2"
              style={{
                background: tab === key ? NAVY : "transparent",
                color: tab === key ? "#FBF6EA" : INK,
                border: `1px solid ${tab === key ? NAVY : "#0a254022"}`,
              }}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {tab === "generator" && <GeneratorTab />}
        {tab === "library"   && <LibraryTab />}
        {tab === "calendar"  && <CalendarTab />}
        {tab === "avatar"    && <AvatarTab />}
        {tab === "top"       && <TopPerformersTab />}
        {tab === "dashboard" && <DashboardTab />}
        {tab === "events"    && <EventsTab />}
        {tab === "controls"  && <ControlsTab />}
      </div>
    </PageShell>
  );
}

// ── Générateur ───────────────────────────────────────────────────────

function GeneratorTab() {
  const [schema, setSchema] = useState(null);
  const [subject, setSubject] = useState("");
  const [keyMsg, setKeyMsg] = useState("");
  const [types, setTypes] = useState(["linkedin_post"]);
  const [tone, setTone] = useState("csf_professionnel");
  const [kids, setKids] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/growth/schema").then(r => setSchema(r.data)); }, []);

  const toggleType = (t) => {
    setTypes((prev) => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  };

  const generate = async () => {
    if (!subject.trim()) return toast.error("Sujet requis.");
    if (!types.length) return toast.error("Au moins un type de contenu.");
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/growth/generate", {
        subject, key_message: keyMsg, content_types: types,
        tone_profile: tone,
        knowledge_item_ids: kids.split(",").map(s => s.trim()).filter(Boolean),
      });
      setResult(data);
      toast.success(`${data.count} contenu(s) généré(s).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Génération échouée");
    } finally { setLoading(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Card className="lg:col-span-1" testid="generator-form">
        <div className="flex items-center gap-2 mb-4">
          <Wand2 size={16} style={{ color: CHAMPAGNE }} />
          <h3 className="font-semibold" style={{ color: INK }}>Nouveau brief</h3>
        </div>
        <FormField label="Sujet">
          <Input value={subject} onChange={e => setSubject(e.target.value)}
                  data-testid="gen-subject"
                  placeholder="Ex : REEE et bonifications 2026" />
        </FormField>
        <FormField label="Message clé">
          <Input value={keyMsg} onChange={e => setKeyMsg(e.target.value)}
                  data-testid="gen-key-message"
                  placeholder="Angle éditorial, promesse centrale" />
        </FormField>
        <FormField label="Types de contenu">
          <div className="flex flex-wrap gap-2">
            {(schema?.content_types || []).map(t => (
              <button key={t} onClick={() => toggleType(t)}
                       data-testid={`gen-type-${t}`}
                       className="px-3 py-1 rounded-full text-xs"
                       style={{
                         background: types.includes(t) ? NAVY : "transparent",
                         color: types.includes(t) ? "#FBF6EA" : SLATE,
                         border: `1px solid ${types.includes(t) ? NAVY : "#0a254022"}`,
                       }}>
                {t}
              </button>
            ))}
          </div>
        </FormField>
        <FormField label="Profil de ton">
          <select value={tone} onChange={e => setTone(e.target.value)}
                    data-testid="gen-tone"
                    className="w-full px-3 py-2 border rounded"
                    style={{ borderColor: "#0a254033" }}>
            {(schema?.tone_profiles || []).map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </FormField>
        <FormField label="IDs Knowledge Items (optionnel, séparés par ,)">
          <Input value={kids} onChange={e => setKids(e.target.value)}
                  data-testid="gen-kids"
                  placeholder="ki_xxx,ki_yyy" />
        </FormField>
        <PrimaryButton onClick={generate} disabled={loading}
                          data-testid="gen-submit">
          {loading ? <Loader /> : <><Sparkles size={14} /> Générer</>}
        </PrimaryButton>
      </Card>

      <Card className="lg:col-span-2" testid="generator-results">
        <h3 className="font-semibold mb-3" style={{ color: INK }}>Résultats</h3>
        {!result && <p className="text-sm" style={{ color: SLATE }}>
          Complétez le brief à gauche, cliquez sur « Générer ».
        </p>}
        {result && result.assets.map((a) => (
          <AssetPreview key={a.id} asset={a} />
        ))}
      </Card>
    </div>
  );
}

// ── Preview d'un asset ──────────────────────────────────────────────

function AssetPreview({ asset, onChange }) {
  const [expanded, setExpanded] = useState(false);
  const [suggestions, setSuggestions] = useState(null);

  const approve = async () => {
    try { await api.post(`/growth/assets/${asset.id}/approve`);
      toast.success("Approuvé."); onChange?.();
    } catch { toast.error("Échec"); }
  };
  const schedule = async () => {
    const iso = prompt("Date de publication (ISO 8601)",
                         new Date(Date.now() + 86400000).toISOString());
    if (!iso) return;
    try { await api.post(`/growth/assets/${asset.id}/schedule`,
                            { scheduled_at: iso });
      toast.success("Planifié."); onChange?.();
    } catch { toast.error("Échec"); }
  };
  const publish = async () => {
    try { await api.post(`/growth/assets/${asset.id}/publish`, {});
      toast.success("Publié."); onChange?.();
    } catch { toast.error("Échec"); }
  };
  const loadSuggestions = async () => {
    try {
      const { data } = await api.get(`/growth/assets/${asset.id}/suggestions`);
      setSuggestions(data.suggestions || []);
    } catch { toast.error("Suggestions indisponibles"); }
  };
  const abStart = async () => {
    try {
      const { data } = await api.post(`/growth/assets/${asset.id}/ab-start`,
                                          { variant_count: 2 });
      toast.success(`${data.count} variantes créées`);
      onChange?.();
    } catch { toast.error("A/B testing échoué"); }
  };
  const renderAvatar = async () => {
    try {
      await api.post("/growth/avatar/render",
                       { script: asset.body.slice(0, 800), asset_id: asset.id });
      toast.success("Rendu vidéo lancé"); onChange?.();
    } catch { toast.error("Rendu vidéo échoué"); }
  };

  const scoreColor = asset.score.global_score >= 80 ? "#0a7f42"
                     : asset.score.global_score >= 60 ? "#c9a34e" : "#c95050";

  return (
    <div className="border rounded-lg p-4 mb-3"
          style={{ borderColor: "#0a254022" }}
          data-testid={`asset-card-${asset.id}`}>
      <div className="flex justify-between items-start gap-3">
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wide mb-1"
                style={{ color: SLATE }}>
            {CHANNEL_LABELS[asset.channel] || asset.channel} · {asset.content_type}
            {asset.ab_variant && ` · Variante ${asset.ab_variant}`}
            {asset.recycled_from && " · Recyclé"}
          </div>
          <div className="font-semibold text-base" style={{ color: INK }}>
            {asset.title}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-2xl font-bold" style={{ color: scoreColor }}>
            {asset.score.global_score}
          </div>
          <div className="text-xs uppercase" style={{ color: SLATE }}>/100</div>
        </div>
      </div>

      <button onClick={() => setExpanded(!expanded)}
                data-testid={`asset-toggle-${asset.id}`}
                className="text-xs mt-2 underline" style={{ color: NAVY }}>
        {expanded ? "Masquer" : "Voir le contenu"}
      </button>

      {expanded && (
        <div className="mt-3">
          <pre className="whitespace-pre-wrap text-sm p-3 rounded"
                style={{ background: "#F7F1E1", color: INK }}
                data-testid={`asset-body-${asset.id}`}>
            {asset.body}
          </pre>
          {!!asset.hashtags?.length && (
            <div className="mt-2 flex flex-wrap gap-1">
              {asset.hashtags.map((h, i) => (
                <span key={i} className="text-xs px-2 py-1 rounded"
                        style={{ background: "#0a254011", color: NAVY }}>{h}</span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-3">
        <span className="px-2 py-1 text-xs rounded"
                style={{ background: STATUS_COLORS[asset.status] + "22",
                         color: STATUS_COLORS[asset.status] }}>
          {asset.status}
        </span>
        <GhostButton onClick={approve} data-testid={`asset-approve-${asset.id}`}>
          <CheckCircle2 size={12} /> Approuver
        </GhostButton>
        <GhostButton onClick={schedule} data-testid={`asset-schedule-${asset.id}`}>
          <Clock size={12} /> Planifier
        </GhostButton>
        <GhostButton onClick={publish} data-testid={`asset-publish-${asset.id}`}>
          <Send size={12} /> Publier
        </GhostButton>
        <GhostButton onClick={loadSuggestions}
                        data-testid={`asset-suggest-${asset.id}`}>
          <Wand2 size={12} /> Suggestions IA
        </GhostButton>
        <GhostButton onClick={abStart} data-testid={`asset-ab-${asset.id}`}>
          <Beaker size={12} /> A/B
        </GhostButton>
        <GhostButton onClick={renderAvatar} data-testid={`asset-avatar-${asset.id}`}>
          <Video size={12} /> Avatar
        </GhostButton>
      </div>

      {suggestions && (
        <div className="mt-3 p-3 rounded" style={{ background: "#c9a34e11" }}>
          <div className="text-xs uppercase mb-2" style={{ color: CHAMPAGNE }}>
            Suggestions d'amélioration
          </div>
          <ul className="list-disc list-inside text-sm" style={{ color: INK }}>
            {suggestions.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Bibliothèque ─────────────────────────────────────────────────────

function LibraryTab() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [channel, setChannel] = useState("");
  const [minScore, setMinScore] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (status) params.set("status", status);
      if (channel) params.set("channel", channel);
      if (minScore) params.set("min_score", minScore);
      const { data } = await api.get(`/growth/assets?${params}`);
      setAssets(data.assets || []);
    } catch { toast.error("Chargement échoué"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div>
      <Card testid="library-filters">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <Input placeholder="Rechercher…" value={q} onChange={e => setQ(e.target.value)}
                  data-testid="lib-q" />
          <select value={status} onChange={e => setStatus(e.target.value)}
                    data-testid="lib-status"
                    className="px-3 py-2 border rounded"
                    style={{ borderColor: "#0a254033" }}>
            <option value="">Tous statuts</option>
            {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={channel} onChange={e => setChannel(e.target.value)}
                    data-testid="lib-channel"
                    className="px-3 py-2 border rounded"
                    style={{ borderColor: "#0a254033" }}>
            <option value="">Tous canaux</option>
            {Object.entries(CHANNEL_LABELS).map(([k, v]) =>
              <option key={k} value={k}>{v}</option>)}
          </select>
          <Input placeholder="Score min" value={minScore} type="number"
                  onChange={e => setMinScore(e.target.value)}
                  data-testid="lib-minscore" />
          <PrimaryButton onClick={load} data-testid="lib-search">
            <Search size={14} /> Filtrer
          </PrimaryButton>
        </div>
      </Card>

      <div className="mt-4">
        {loading ? <Loader /> :
          assets.length === 0 ? (
            <Alert type="info">Aucun contenu trouvé.</Alert>
          ) : assets.map(a => <AssetPreview key={a.id} asset={a} onChange={load} />)
        }
      </div>
    </div>
  );
}

// ── Calendrier éditorial ───────────────────────────────────────────

function CalendarTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/growth/calendar").then(r => setEntries(r.data.entries || []))
       .catch(() => toast.error("Chargement calendrier échoué"))
       .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    entries.forEach(e => {
      const d = (e.scheduled_at || e.published_at || "").slice(0, 10) || "Sans date";
      (g[d] ||= []).push(e);
    });
    return g;
  }, [entries]);

  return (
    <Card testid="calendar-root">
      {loading ? <Loader /> : Object.keys(grouped).length === 0 ? (
        <Alert type="info">Aucune publication planifiée pour l'instant.</Alert>
      ) : (
        Object.entries(grouped).sort().map(([date, list]) => (
          <div key={date} className="mb-4">
            <div className="text-xs uppercase tracking-widest mb-2"
                  style={{ color: CHAMPAGNE }}>{date}</div>
            {list.map(e => (
              <div key={e.id} className="p-3 mb-2 rounded border"
                    style={{ borderColor: "#0a254022" }}
                    data-testid={`cal-entry-${e.id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium" style={{ color: INK }}>{e.title}</div>
                    <div className="text-xs" style={{ color: SLATE }}>
                      {CHANNEL_LABELS[e.channel] || e.channel} · {e.status}
                    </div>
                  </div>
                  <div className="text-lg font-bold"
                        style={{ color: e.global_score >= 80 ? "#0a7f42" : NAVY }}>
                    {e.global_score}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))
      )}
    </Card>
  );
}

// ── Avatar ──────────────────────────────────────────────────────────

function AvatarTab() {
  const [script, setScript] = useState("");
  const [title, setTitle] = useState("");
  const [jobs, setJobs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTpl, setSelectedTpl] = useState("");
  const [rendering, setRendering] = useState(false);
  const [q, setQ] = useState("");
  const [favOnly, setFavOnly] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [failedCount, setFailedCount] = useState(0);
  const [humanize, setHumanize] = useState(true);
  const [toneHint, setToneHint] = useState("");
  const [humanizedPreview, setHumanizedPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [creditAlert, setCreditAlert] = useState(null);
  const [costSummary, setCostSummary] = useState(null);

  const loadCreditAlerts = () => api.get("/growth/avatar/credit-alerts")
                                        .then(r => setCreditAlert(r.data))
                                        .catch(() => {});
  const loadCostSummary = () => api.get("/growth/avatar/cost-summary?months=3")
                                       .then(r => setCostSummary(r.data))
                                       .catch(() => {});

  const load = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (favOnly) p.set("favorites_only", "true");
    if (showDeleted) p.set("include_deleted", "true");
    return api.get(`/growth/avatar/jobs?${p}`)
              .then(r => {
                setJobs(r.data.jobs || []);
                setFailedCount((r.data.jobs || []).filter(
                  j => j.status === "failed" && !j.deleted).length);
              })
              .catch(() => {});
  };
  // Rafraîchit d'un coup tous les jobs bloqués en rendering (HeyGen a peut-être
  // fini pendant que l'onglet était fermé)
  const refreshPending = () => api.post("/growth/avatar/jobs/refresh-pending")
                                      .then(() => load())
                                      .catch(() => {});
  const loadTemplates = () => api.get("/growth/avatar/templates")
                                    .then(r => setTemplates(r.data.templates || []))
                                    .catch(() => {});
  useEffect(() => { load(); }, [q, favOnly, showDeleted]); // eslint-disable-line
  useEffect(() => {
    loadTemplates();
    refreshPending();
    loadCreditAlerts();
    loadCostSummary();
    // Auto-refresh périodique tant que la page est ouverte
    const t = setInterval(() => {
      refreshPending();
      loadCreditAlerts();
      loadCostSummary();
    }, 60000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line

  const applyTemplate = (tplId) => {
    setSelectedTpl(tplId);
    const t = templates.find(x => x.id === tplId);
    if (t) {
      setScript(t.script);
      if (!title) setTitle(t.name);
    }
  };

  const bulkRetry = async () => {
    if (!window.confirm(`Régénérer ${Math.min(failedCount, 5)} vidéos échouées ?`)) return;
    try {
      const { data } = await api.post("/growth/avatar/jobs/bulk-retry?max_jobs=5");
      toast.success(`${data.retried} régénérations lancées`);
      load();
    } catch { toast.error("Échec bulk retry"); }
  };

  const purgeZombies = async () => {
    if (!window.confirm(
      "Purger les vidéos zombies ?\n\n" +
      "Sont supprimées :\n" +
      "• vidéos en 'rendering' bloquées depuis > 30 min sans ID HeyGen\n" +
      "• vidéos en 'rendering' > 3h même avec ID HeyGen\n" +
      "• vidéos 'failed' > 24h qui n'ont jamais atteint HeyGen\n\n" +
      "Les vidéos restent dans la corbeille (Afficher supprimées).")) return;
    try {
      const { data } = await api.post("/growth/avatar/jobs/purge-zombies");
      toast.success(data.purged > 0
        ? `${data.purged} vidéo${data.purged > 1 ? "s" : ""} zombie${data.purged > 1 ? "s" : ""} purgée${data.purged > 1 ? "s" : ""}`
        : "Aucune vidéo zombie détectée");
      load();
    } catch { toast.error("Échec purge"); }
  };

  const render = async () => {
    if (!script.trim()) return toast.error("Script requis");
    setRendering(true);
    try {
      const { data } = await api.post("/growth/avatar/test", {
        script, humanize, tone_hint: toneHint || null,
      });
      if (title.trim() && data.id) {
        await api.patch(`/growth/avatar/jobs/${data.id}`,
                           { title: title.trim() });
      }
      toast.success(`Rendu ${data.status}${data.humanized ? " · voix humaine" : ""}`);
      setScript(""); setTitle(""); setSelectedTpl(""); setHumanizedPreview(null);
      load(); loadCreditAlerts(); loadCostSummary();
      if (data.status === "rendering") pollUntilReady(data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
    finally { setRendering(false); }
  };

  const previewHumanize = async () => {
    if (!script.trim()) return toast.error("Script requis pour la prévisualisation");
    setPreviewing(true);
    try {
      const { data } = await api.post("/growth/avatar/humanize-preview", {
        script, tone_hint: toneHint || null, use_llm: true,
      });
      setHumanizedPreview(data);
      toast.success(`Réécrit via ${data.engine === "llm" ? "Claude Sonnet 4.6" : "règles"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
    finally { setPreviewing(false); }
  };

  const pollUntilReady = (jobId, attempts = 0) => {
    // 45 tentatives × 20 s = 15 minutes max de polling (HeyGen prend
    // parfois 8-10 min pour des vidéos > 1 min). Au-delà, le scheduler
    // backend prend le relais (refresh_pending_avatar_jobs toutes les 90 s).
    if (attempts > 45) return;
    setTimeout(async () => {
      try {
        const { data } = await api.post(`/growth/avatar/jobs/${jobId}/refresh`);
        if (data.status === "rendering") pollUntilReady(jobId, attempts + 1);
        else if (data.status === "ready") { toast.success("Vidéo prête ! 🎬"); load(); }
        else if (data.status === "failed") { toast.error("Rendu échoué"); load(); }
      } catch {}
    }, 20000);
  };

  const useAsTemplate = (job) => {
    setScript(job.script || "");
    setTitle(job.title ? `${job.title} — variante` : "");
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.message("Script chargé — modifiez-le puis cliquez Générer");
  };

  return (
    <div className="space-y-6">
      {/* Bandeau alerte crédits HeyGen */}
      {creditAlert?.has_alert && (
        <div className="rounded-lg p-4 flex items-start gap-3"
              style={{ background: "#fef3c7", border: "2px solid #f59e0b" }}
              data-testid="credit-alert-banner">
          <div className="text-2xl leading-none">⚠️</div>
          <div className="flex-1">
            <div className="font-semibold text-sm mb-1" style={{ color: "#78350f" }}>
              Crédits API HeyGen · Solde : {creditAlert.api_quota ?? "?"}
              {creditAlert.studio_credits > 0 && (
                <span className="text-xs font-normal ml-2" style={{ color: "#92400e" }}>
                  (dont {creditAlert.studio_credits} gratuits Studio Web — inutilisables via API)
                </span>
              )}
            </div>
            <div className="text-xs mb-2" style={{ color: "#92400e" }}>
              {creditAlert.message}
              {creditAlert.count > 0 && (
                <> · {creditAlert.count} génération{creditAlert.count > 1 ? "s" : ""} bloquée{creditAlert.count > 1 ? "s" : ""} récemment.</>
              )}
            </div>
            <a href={creditAlert.topup_url || "https://app.heygen.com/pricing"}
                target="_blank" rel="noreferrer"
                data-testid="credit-alert-topup"
                className="inline-block px-3 py-1.5 rounded text-xs font-semibold"
                style={{ background: "#f59e0b", color: "white" }}>
              → Souscrire à un plan avec accès API
            </a>
          </div>
          <button onClick={() => setCreditAlert(null)}
                    data-testid="credit-alert-dismiss"
                    className="text-lg leading-none px-1"
                    style={{ color: "#78350f" }}>×</button>
        </div>
      )}

      {/* Carte Cost Tracker */}
      {costSummary && (
        <Card testid="cost-tracker-card">
          <div className="flex items-center gap-2 mb-3">
            <span style={{ color: CHAMPAGNE, fontSize: 16 }}>💰</span>
            <h3 className="font-semibold" style={{ color: INK }}>Coûts vidéo · estimation</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 rounded" style={{ background: "#fdf9ee" }}>
              <div className="text-xs mb-1" style={{ color: SLATE }}>Ce mois-ci</div>
              <div className="text-2xl font-bold" style={{ color: INK }}
                    data-testid="cost-current-month">
                ${(costSummary.current_month?.total || 0).toFixed(2)}
              </div>
              <div className="text-xs mt-1" style={{ color: SLATE }}>
                {costSummary.current_month?.count || 0} vidéo{(costSummary.current_month?.count || 0) > 1 ? "s" : ""}
              </div>
            </div>
            <div className="p-3 rounded" style={{ background: "#f5f5f5" }}>
              <div className="text-xs mb-1" style={{ color: SLATE }}>Mois précédent</div>
              <div className="text-2xl font-bold" style={{ color: SLATE }}
                    data-testid="cost-last-month">
                ${(costSummary.last_month?.total || 0).toFixed(2)}
              </div>
              <div className="text-xs mt-1" style={{ color: SLATE }}>
                {costSummary.last_month?.count || 0} vidéo{(costSummary.last_month?.count || 0) > 1 ? "s" : ""}
              </div>
            </div>
            <div className="p-3 rounded" style={{ background: "#eef7fb" }}>
              <div className="text-xs mb-1" style={{ color: SLATE }}>Voix · ce mois</div>
              <div className="text-lg font-bold" style={{ color: INK }}>
                ${(costSummary.current_month?.voice || 0).toFixed(2)}
              </div>
              <div className="text-xs mt-1" style={{ color: SLATE }}>ElevenLabs</div>
            </div>
            <div className="p-3 rounded" style={{ background: "#f2ede2" }}>
              <div className="text-xs mb-1" style={{ color: SLATE }}>Vidéo · ce mois</div>
              <div className="text-lg font-bold" style={{ color: INK }}>
                ${(costSummary.current_month?.video || 0).toFixed(2)}
              </div>
              <div className="text-xs mt-1" style={{ color: SLATE }}>HeyGen</div>
            </div>
          </div>
          {costSummary.months?.length > 0 && (
            <div className="mt-3 text-xs" style={{ color: SLATE }}>
              Cumul {costSummary.months.length} mois : <b>${(costSummary.totals?.total || 0).toFixed(2)}</b>
              &nbsp;·&nbsp; {costSummary.totals?.count || 0} vidéo{(costSummary.totals?.count || 0) > 1 ? "s" : ""} au total
            </div>
          )}
          <p className="text-xs mt-2" style={{ color: SLATE, opacity: 0.7 }}>
            Estimations : HeyGen ≈ $0.30/min · ElevenLabs ≈ $0.30/1000 caractères. Coûts réels facturés par les providers.
          </p>
        </Card>
      )}

      {/* Générateur */}
      <Card testid="avatar-generator">
        <div className="flex items-center gap-2 mb-3">
          <Video size={16} style={{ color: CHAMPAGNE }} />
          <h3 className="font-semibold" style={{ color: INK }}>Nouvelle vidéo</h3>
        </div>

        <FormField label="Template pré-écrit (optionnel)">
          <select value={selectedTpl}
                    onChange={e => applyTemplate(e.target.value)}
                    data-testid="avatar-template-select"
                    className="w-full px-3 py-2 border rounded"
                    style={{ borderColor: "#0a254033" }}>
            <option value="">— Aucun template —</option>
            {templates.map(t => (
              <option key={t.id} value={t.id}>{t.name} · {t.category}</option>
            ))}
          </select>
        </FormField>

        <FormField label="Titre (optionnel)">
          <Input value={title} onChange={e => setTitle(e.target.value)}
                  data-testid="avatar-title"
                  placeholder="Ex : REEE bonification 2026 · Post LinkedIn" />
        </FormField>
        <FormField label="Script">
          <textarea value={script} onChange={e => setScript(e.target.value)}
                      data-testid="avatar-script" rows={6}
                      placeholder="Rédigez votre script ou choisissez un template…"
                      className="w-full px-3 py-2 border rounded text-sm"
                      style={{ borderColor: "#0a254033" }} />
        </FormField>

        {/* Voix ultra-humaine */}
        <div className="border rounded-lg p-3 mb-3"
              style={{ borderColor: "#c9a34e55", background: "#fdf9ee" }}>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={humanize}
                    onChange={e => setHumanize(e.target.checked)}
                    data-testid="avatar-humanize-toggle"
                    className="mt-1" />
            <div className="flex-1">
              <div className="font-semibold text-sm" style={{ color: INK }}>
                Voix ultra-humaine (recommandé)
              </div>
              <div className="text-xs mt-1" style={{ color: SLATE }}>
                Réécrit ton script en dialogue oral, ajoute pauses, respirations
                et micro-hésitations pour une voix indiscernable d'un humain.
                Utilise Claude Sonnet 4.6.
              </div>
            </div>
          </label>
          {humanize && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium block mb-1"
                        style={{ color: SLATE }}>
                  Ton (auto si vide)
                </label>
                <select value={toneHint}
                          onChange={e => setToneHint(e.target.value)}
                          data-testid="avatar-tone-hint"
                          className="w-full px-3 py-2 border rounded text-sm"
                          style={{ borderColor: "#0a254033" }}>
                  <option value="">— Détection automatique —</option>
                  <option value="actualite">Actualité / alerte (rythme rapide)</option>
                  <option value="conseil">Conseil / pédagogie (posé, réfléchi)</option>
                  <option value="cas_client">Cas client (chaleureux, empathique)</option>
                  <option value="analyse">Analyse chiffrée (calme, précis)</option>
                  <option value="enthousiasme">Nouveauté (énergique)</option>
                </select>
              </div>
              <div className="flex items-end">
                <GhostButton onClick={previewHumanize} disabled={previewing}
                                data-testid="avatar-humanize-preview">
                  {previewing ? <Loader /> : <>👂 Prévisualiser la réécriture</>}
                </GhostButton>
              </div>
            </div>
          )}
          {humanizedPreview && (
            <div className="mt-3 border-t pt-3"
                  style={{ borderColor: "#c9a34e33" }}>
              <div className="text-xs font-semibold mb-1"
                    style={{ color: "#c9a34e" }}>
                Version orale (moteur : {humanizedPreview.engine} ·
                ratio longueur ×{humanizedPreview.length_ratio}) :
              </div>
              <div className="text-sm whitespace-pre-wrap p-3 rounded"
                    style={{ color: INK, background: "white",
                              maxHeight: 240, overflowY: "auto" }}
                    data-testid="avatar-humanize-preview-text">
                {humanizedPreview.humanized}
              </div>
              <div className="text-xs mt-2" style={{ color: SLATE }}>
                Les balises <code>&lt;break time="0.4s"/&gt;</code> sont
                interprétées par ElevenLabs comme des pauses réelles.
              </div>
            </div>
          )}
        </div>

        <PrimaryButton onClick={render} disabled={rendering}
                          data-testid="avatar-submit">
          {rendering ? <Loader /> : <><Sparkles size={14} /> Générer la vidéo</>}
        </PrimaryButton>
        <p className="text-xs mt-3" style={{ color: SLATE }}>
          Pipeline : {humanize && "humanisation → "}script → ElevenLabs → HeyGen → MP4 HD.
          Rendu ~1-2 minutes.
        </p>
      </Card>

      {/* Filtres + bulk retry */}
      <Card testid="avatar-filters">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <Input placeholder="Rechercher (titre / script)…" value={q}
                  onChange={e => setQ(e.target.value)} data-testid="avatar-q" />
          <label className="flex items-center gap-2 text-sm" style={{ color: INK }}>
            <input type="checkbox" checked={favOnly}
                    onChange={e => setFavOnly(e.target.checked)}
                    data-testid="avatar-fav-filter" />
            ⭐ Favoris
          </label>
          <label className="flex items-center gap-2 text-sm" style={{ color: SLATE }}>
            <input type="checkbox" checked={showDeleted}
                    onChange={e => setShowDeleted(e.target.checked)}
                    data-testid="avatar-deleted-filter" />
            Inclus supprimés
          </label>
          <GhostButton onClick={load} data-testid="avatar-refresh-all">
            <RefreshCw size={12} /> Rafraîchir
          </GhostButton>
          {failedCount > 0 && (
            <PrimaryButton onClick={bulkRetry} data-testid="avatar-bulk-retry">
              ↺ Retry {failedCount} échec{failedCount > 1 ? "s" : ""}
            </PrimaryButton>
          )}
          <GhostButton onClick={purgeZombies} data-testid="avatar-purge-zombies">
            🧹 Purger zombies
          </GhostButton>
        </div>
      </Card>

      {/* Historique */}
      <Card testid="avatar-history">
        <h3 className="font-semibold mb-3" style={{ color: INK }}>
          Bibliothèque vidéos ({jobs.length})
        </h3>
        {jobs.length === 0 && <Alert type="info">Aucune vidéo pour l'instant.</Alert>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {jobs.map(j => <VideoCard key={j.id} job={j} onChange={load}
                                        onUseAsTemplate={useAsTemplate} />)}
        </div>
      </Card>
    </div>
  );
}


function VideoCard({ job, onChange, onUseAsTemplate }) {
  const [editing, setEditing] = useState(false);
  const [newTitle, setNewTitle] = useState(job.title || "");

  const saveTitle = async () => {
    try {
      await api.patch(`/growth/avatar/jobs/${job.id}`, { title: newTitle });
      toast.success("Renommé");
      setEditing(false); onChange();
    } catch { toast.error("Échec"); }
  };

  const toggleFav = async () => {
    try {
      await api.patch(`/growth/avatar/jobs/${job.id}`, { favorite: !job.favorite });
      onChange();
    } catch { toast.error("Échec"); }
  };

  const del = async () => {
    if (!window.confirm("Supprimer cette vidéo de l'historique ?")) return;
    try {
      await api.delete(`/growth/avatar/jobs/${job.id}`);
      toast.success("Supprimé"); onChange();
    } catch { toast.error("Échec"); }
  };

  const regen = async () => {
    if (!window.confirm("Régénérer cette vidéo (nouveau job) ?")) return;
    try {
      await api.post(`/growth/avatar/jobs/${job.id}/regenerate`, {});
      toast.success("Régénération lancée"); onChange();
    } catch { toast.error("Échec"); }
  };

  const refresh = async () => {
    try {
      await api.post(`/growth/avatar/jobs/${job.id}/refresh`);
      onChange();
    } catch { toast.error("Échec refresh"); }
  };

  const download = async () => {
    try {
      const resp = await api.get(`/growth/avatar/jobs/${job.id}/download`,
                                     { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      const fname = (job.title || "altiora").replace(/[^a-z0-9]/gi, "_") + ".mp4";
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Téléchargement démarré");
    } catch { toast.error("Échec téléchargement"); }
  };

  return (
    <div className="rounded-lg border p-4 flex flex-col"
          style={{ borderColor: "#0a254022",
                   opacity: job.deleted ? 0.5 : 1,
                   background: "#ffffff" }}
          data-testid={`video-card-${job.id}`}>
      {/* Header */}
      <div className="flex justify-between items-start gap-2 mb-2">
        {editing ? (
          <div className="flex-1 flex gap-1">
            <Input value={newTitle} onChange={e => setNewTitle(e.target.value)}
                    data-testid={`video-title-input-${job.id}`}
                    placeholder="Nom de la vidéo" />
            <GhostButton onClick={saveTitle}
                            data-testid={`video-title-save-${job.id}`}>OK</GhostButton>
          </div>
        ) : (
          <div className="flex-1">
            <div className="font-medium truncate" style={{ color: INK }}
                  data-testid={`video-title-${job.id}`}>
              {job.title || <span style={{ color: SLATE }}>Sans titre — {job.script?.slice(0, 40)}…</span>}
            </div>
            <div className="text-xs" style={{ color: SLATE }}>
              {new Date(job.created_at).toLocaleString("fr-CA")} · {job.provider}
              {job.duration_seconds > 0 && ` · ${job.duration_seconds.toFixed(1)}s`}
            </div>
          </div>
        )}
        <button onClick={toggleFav}
                  data-testid={`video-fav-${job.id}`}
                  className="text-lg"
                  style={{ color: job.favorite ? CHAMPAGNE : "#0a254033" }}>
          {job.favorite ? "★" : "☆"}
        </button>
      </div>

      {/* Status */}
      <span className="px-2 py-1 text-xs rounded self-start mb-2"
              style={{ background: (STATUS_COLORS[job.status] || SLATE) + "22",
                       color: STATUS_COLORS[job.status] || SLATE }}>
        {job.status}
      </span>

      {/* Video */}
      {job.video_url ? (
        <video controls className="w-full rounded max-h-64 bg-black mb-2"
                src={job.video_url}
                data-testid={`video-player-${job.id}`} />
      ) : job.thumbnail_url ? (
        <img src={job.thumbnail_url} alt="thumb"
              className="w-full rounded max-h-64 object-cover mb-2" />
      ) : (
        <div className="w-full h-32 rounded bg-[#F7F1E1] flex items-center justify-center mb-2 text-xs"
              style={{ color: SLATE }}>
          {job.status === "rendering" ? "🎬 Rendu en cours…" : "Pas de prévisualisation"}
        </div>
      )}

      {job.error && (
        <div className="text-xs mb-2 p-2 rounded"
              style={{ background: "#c9505011", color: "#c95050" }}>
          {job.error}
          {/^cr[ée]dits heygen [ée]puis[ée]s/i.test(job.error) && (
            <a href="https://app.heygen.com/pricing" target="_blank" rel="noreferrer"
                className="ml-2 underline font-semibold"
                data-testid={`heygen-topup-${job.id}`}>
              → Recharger
            </a>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2 mt-auto">
        {job.video_url && (
          <GhostButton onClick={download}
                          data-testid={`video-download-${job.id}`}>
            ⬇ MP4
          </GhostButton>
        )}
        {job.status === "rendering" && (
          <GhostButton onClick={refresh}
                          data-testid={`video-refresh-${job.id}`}>
            ↻ Rafraîchir
          </GhostButton>
        )}
        <GhostButton onClick={() => setEditing(!editing)}
                        data-testid={`video-rename-${job.id}`}>
          ✎ Renommer
        </GhostButton>
        <GhostButton onClick={regen} data-testid={`video-regen-${job.id}`}>
          ↺ Régénérer
        </GhostButton>
        {onUseAsTemplate && job.script && (
          <GhostButton onClick={() => onUseAsTemplate(job)}
                          data-testid={`video-use-tpl-${job.id}`}>
            📋 Comme template
          </GhostButton>
        )}
        {!job.deleted && (
          <GhostButton onClick={del} data-testid={`video-delete-${job.id}`}>
            🗑 Supprimer
          </GhostButton>
        )}
      </div>
    </div>
  );
}


// ── Top performers & recycling ─────────────────────────────────────

function TopPerformersTab() {
  const [top, setTop] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/growth/top-performers?limit=10")
       .then(r => setTop(r.data.assets || []))
       .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const recycle = async (id) => {
    const chan = prompt("Canaux cibles (séparés par ,) : linkedin,facebook,email,x", "linkedin,facebook");
    if (!chan) return;
    try {
      const { data } = await api.post(`/growth/assets/${id}/recycle`,
                                          { channels: chan.split(",").map(s => s.trim()) });
      toast.success(`${data.count} recyclages créés`);
    } catch { toast.error("Échec recyclage"); }
  };

  return (
    <Card testid="top-root">
      <div className="flex items-center gap-2 mb-3">
        <Trophy size={16} style={{ color: CHAMPAGNE }} />
        <h3 className="font-semibold" style={{ color: INK }}>Top performers</h3>
      </div>
      {loading ? <Loader /> : top.length === 0 ? (
        <Alert type="info">Aucun contenu publié avec performances pour l'instant.</Alert>
      ) : top.map(a => (
        <div key={a.id} className="p-3 mb-2 rounded border flex justify-between items-center"
              style={{ borderColor: "#0a254022" }}
              data-testid={`top-item-${a.id}`}>
          <div className="flex-1">
            <div className="font-medium" style={{ color: INK }}>{a.title}</div>
            <div className="text-xs" style={{ color: SLATE }}>
              {CHANNEL_LABELS[a.channel]} · Score {a.score.global_score}/100 ·
              {" "}{a.performance?.leads || 0} leads · {a.performance?.revenue || 0} $ revenus
            </div>
          </div>
          <GhostButton onClick={() => recycle(a.id)}
                          data-testid={`top-recycle-${a.id}`}>
            <RefreshCw size={12} /> Recycler
          </GhostButton>
        </div>
      ))}
    </Card>
  );
}

// ── Dashboard ────────────────────────────────────────────────────

function DashboardTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/growth/dashboard").then(r => setStats(r.data)); }, []);
  if (!stats) return <Loader />;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Card testid="dash-total">
        <div className="text-xs uppercase" style={{ color: SLATE }}>Total contenus</div>
        <div className="text-3xl font-bold" style={{ color: NAVY }}>
          {stats.total_generated}
        </div>
      </Card>
      <Card testid="dash-perf">
        <div className="text-xs uppercase" style={{ color: SLATE }}>Vues cumulées</div>
        <div className="text-3xl font-bold" style={{ color: NAVY }}>
          {stats.performance_total?.views || 0}
        </div>
        <div className="text-xs mt-1" style={{ color: SLATE }}>
          {stats.performance_total?.leads || 0} leads · {stats.performance_total?.revenue || 0} $
        </div>
      </Card>
      <Card testid="dash-status">
        <div className="text-xs uppercase mb-2" style={{ color: SLATE }}>Par statut</div>
        {Object.entries(stats.by_status || {}).map(([k, v]) => (
          <div key={k} className="flex justify-between text-sm py-1">
            <span style={{ color: STATUS_COLORS[k] }}>{k}</span>
            <span style={{ color: INK }}>{v}</span>
          </div>
        ))}
      </Card>

      <Card className="md:col-span-3" testid="dash-channels">
        <h3 className="font-semibold mb-3" style={{ color: INK }}>ROI par canal</h3>
        {(stats.roi_by_channel || []).length === 0 && (
          <p className="text-sm" style={{ color: SLATE }}>
            Aucune performance mesurée. Marquez des contenus comme publiés avec leurs KPI pour alimenter cette vue.
          </p>
        )}
        {(stats.roi_by_channel || []).map(r => (
          <div key={r.channel} className="flex justify-between text-sm py-2 border-b"
                style={{ borderColor: "#0a254011" }}>
            <span>{CHANNEL_LABELS[r.channel] || r.channel}</span>
            <span style={{ color: SLATE }}>
              {r.views} vues · {r.leads} leads ·
              <strong style={{ color: NAVY }}> {r.revenue} $</strong>
            </span>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ── Events (journal) ───────────────────────────────────────────

function EventsTab() {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get("/growth/events?limit=200").then(r => setEvents(r.data.events || []));
  }, []);
  return (
    <Card testid="events-root">
      <h3 className="font-semibold mb-3" style={{ color: INK }}>Journal des automatisations</h3>
      {events.length === 0 && <Alert type="info">Aucun événement.</Alert>}
      <div className="max-h-[600px] overflow-auto">
        {events.map(e => (
          <div key={e.id} className="py-2 border-b text-sm flex justify-between"
                style={{ borderColor: "#0a254011" }}
                data-testid={`event-${e.id}`}>
            <div>
              <span className="uppercase text-xs mr-2" style={{ color: CHAMPAGNE }}>{e.kind}</span>
              <span style={{ color: INK }}>{JSON.stringify(e.detail).slice(0, 120)}</span>
            </div>
            <span className="text-xs shrink-0 ml-2" style={{ color: SLATE }}>
              {new Date(e.created_at).toLocaleString("fr-CA")}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Controls (automation) ──────────────────────────────────────

function ControlsTab() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/growth/controls").then(r => setC(r.data));
  }, []);
  if (!c) return <Loader />;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/growth/controls", c);
      toast.success("Réglages enregistrés");
    } catch { toast.error("Échec"); }
    finally { setSaving(false); }
  };
  const toggle = (k) => setC({ ...c, [k]: !c[k] });
  const toggleChan = (ch) => setC({ ...c,
    channels_enabled: { ...c.channels_enabled, [ch]: !c.channels_enabled[ch] } });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card testid="controls-general">
        <h3 className="font-semibold mb-3" style={{ color: INK }}>Niveau d'automatisation</h3>
        <select value={c.level} onChange={e => setC({ ...c, level: e.target.value })}
                  data-testid="ctrl-level"
                  className="w-full px-3 py-2 border rounded mb-3"
                  style={{ borderColor: "#0a254033" }}>
          {["manual", "semi_auto", "smart_auto", "full_auto"].map(x =>
            <option key={x} value={x}>{x}</option>)}
        </select>
        <FormField label="Seuil score auto-approbation">
          <Input type="number" value={c.auto_threshold}
                  onChange={e => setC({ ...c, auto_threshold: Number(e.target.value) })}
                  data-testid="ctrl-threshold" />
        </FormField>
        <FormField label="LLM modèle">
          <Input value={c.llm_model}
                  onChange={e => setC({ ...c, llm_model: e.target.value })}
                  data-testid="ctrl-llm-model" />
        </FormField>
        <FormField label="LLM fournisseur">
          <Input value={c.llm_provider}
                  onChange={e => setC({ ...c, llm_provider: e.target.value })}
                  data-testid="ctrl-llm-provider" />
        </FormField>
        <FormField label="Avatar fournisseur">
          <Input value={c.avatar_provider}
                  onChange={e => setC({ ...c, avatar_provider: e.target.value })}
                  data-testid="ctrl-avatar-provider" />
        </FormField>
      </Card>

      <Card testid="controls-toggles">
        <h3 className="font-semibold mb-3" style={{ color: INK }}>Composants</h3>
        {["generation_enabled", "avatar_videos_enabled", "carousels_enabled",
          "case_studies_enabled", "articles_enabled", "emails_enabled",
          "scheduling_enabled", "publishing_enabled", "comment_replies_enabled",
          "recycling_enabled", "ab_testing_enabled", "learning_enabled"].map(k => (
          <label key={k} className="flex items-center justify-between py-2 text-sm">
            <span style={{ color: INK }}>{k.replace(/_/g, " ")}</span>
            <input type="checkbox" checked={!!c[k]} onChange={() => toggle(k)}
                    data-testid={`ctrl-${k}`} />
          </label>
        ))}
      </Card>

      <Card className="lg:col-span-2" testid="controls-channels">
        <h3 className="font-semibold mb-3" style={{ color: INK }}>Canaux activés</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {Object.entries(c.channels_enabled).map(([ch, on]) => (
            <label key={ch} className="flex items-center justify-between p-2 border rounded"
                    style={{ borderColor: "#0a254022" }}>
              <span style={{ color: INK }}>{CHANNEL_LABELS[ch] || ch}</span>
              <input type="checkbox" checked={on} onChange={() => toggleChan(ch)}
                      data-testid={`ctrl-channel-${ch}`} />
            </label>
          ))}
        </div>
        <div className="mt-4">
          <PrimaryButton onClick={save} disabled={saving}
                            data-testid="ctrl-save">
            {saving ? <Loader /> : "Enregistrer"}
          </PrimaryButton>
        </div>
      </Card>
    </div>
  );
}
